import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, App, Button, Card, Col, Collapse, ColorPicker, Form, Input, InputNumber, Popconfirm,
  Row, Select, Space, Spin, Tag, Typography, Upload,
} from 'antd';
import { ArrowLeftOutlined, EyeOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { hataMesaji, markaApi } from './markaApi';
import LogoKarti, { YONELIM_ADI } from './LogoKarti';
import Yuva from './Yuva';

const { Title, Text } = Typography;

const renkAl = (c) => (typeof c === 'string' ? c : c?.toHexString?.().toUpperCase());
const RenkSec = (props) => <ColorPicker disabledAlpha showText format="hex" {...props} />;

const BILGI = [
  ['ad', 'Görünen ad', true], ['unvan', 'Ticari unvan'], ['sektor', 'Sektör'], ['sehir', 'Şehir'],
  ['adres', 'Adres'], ['telefon', 'Telefon'], ['eposta', 'E-posta'], ['web', 'Web'],
  ['sosyal', 'Sosyal medya'], ['slogan', 'Slogan'], ['vergi', 'Vergi bilgisi'],
];
const SABLON_ADI = { genel: 'Genel (Royal Drive düzeni)', atmpoint: 'ATMPOINT', reaktifbeton: 'Reaktif Beton', deluxe: 'Deluxe' };

/** Tek marka: bilgiler, logolar, dosyalar, kontrol listesi ve yayın durumu. */
export default function MarkaDuzenle({ slug, onGeri, onOnizle, onDegisti }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [v, setV] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [yeniFont, setYeniFont] = useState('');

  const yukle = useCallback(async () => {
    try {
      const c = await markaApi.marka(slug);
      setV(c);
      form.setFieldsValue({ ...c.marka, palet: c.marka.palet || [] });
    } catch (e) { message.error(hataMesaji(e), 6); }
  }, [slug, form, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const degisti = () => { yukle(); onDegisti?.(); };

  const kaydet = async () => {
    const d = await form.validateFields();
    ['renk_ana', 'renk_ikincil', 'renk_vurgu'].forEach((k) => { d[k] = renkAl(d[k]) || null; });
    d.palet = (d.palet || []).filter((p) => p && p.name).map((p) => ({ name: p.name, hex: renkAl(p.hex) }));
    setKaydediliyor(true);
    try { setV(await markaApi.guncelle(slug, d)); message.success('Kaydedildi'); onDegisti?.(); }
    catch (e) { message.error(hataMesaji(e), 6); } finally { setKaydediliyor(false); }
  };

  const durumIslemi = async (is, mesaj) => {
    try { await is(); message.success(mesaj); degisti(); } catch (e) { message.error(hataMesaji(e), 8); }
  };

  if (!v) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  const { marka: m, logolar, dosyalar, kontrol, katalog } = v;
  const yuva = (y, etiket, ek = {}) => <Yuva key={y} slug={slug} yuva={y} etiket={etiket} dosya={dosyalar[y]} onDegisti={degisti} {...ek} />;
  const fontlar = Object.keys(dosyalar).filter((y) => y.startsWith('font/'));

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onGeri}>Markalar</Button>
          <Title level={4} style={{ margin: 0 }}>{m.ad}</Title>
          <Text type="secondary">({m.slug})</Text>
          {m.durum === 'yayinda' ? <Tag color="green">Yayında</Tag> : <Tag color="orange">Taslak</Tag>}
        </Space>
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => onOnizle(slug)} disabled={!logolar.length}>Portalda önizle</Button>
          {m.durum === 'yayinda' ? (
            <Popconfirm title="Marka portaldan gizlensin mi?" onConfirm={() => durumIslemi(() => markaApi.taslagaAl(slug), 'Taslağa alındı')}>
              <Button>Taslağa al</Button>
            </Popconfirm>
          ) : (
            <>
              <Button type="primary" disabled={kontrol.zorunlu.length > 0}
                onClick={() => durumIslemi(() => markaApi.yayinla(slug), 'Yayınlandı')}>Yayınla</Button>
              <Popconfirm title="Taslak marka pasife alınsın mı? (Dosyalar silinmez)"
                onConfirm={() => markaApi.pasif(slug)
                  .then(() => { message.success('Pasife alındı'); onDegisti?.(); onGeri(); })
                  .catch((e) => message.error(hataMesaji(e), 8))}>
                <Button danger>Pasife al</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      </Space>

      {kontrol.zorunlu.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Yayınlamak için zorunlu eksikler"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{kontrol.zorunlu.map((z) => <li key={z}>{z}</li>)}</ul>} />
      )}
      {m.durum === 'yayinda' && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Bu marka yayında: burada yaptığınız değişiklikler portala hemen yansır. Büyük bir düzenleme öncesi 'Taslağa al' ile gizleyebilirsiniz." />
      )}
      {kontrol.uyari.length > 0 && (
        <Collapse size="small" style={{ marginBottom: 12 }} items={[{
          key: 'u', label: `${kontrol.uyari.length} eksik / uyarı (yayını engellemez)`,
          children: <ul style={{ margin: 0, paddingLeft: 18, columns: 2 }}>{kontrol.uyari.map((u) => <li key={u}>{u}</li>)}</ul>,
        }]} />
      )}

      <Collapse defaultActiveKey={['bilgi', 'logo']} items={[
        {
          key: 'bilgi', label: 'Bilgiler, renkler, fontlar, kartvizit ve imza',
          children: (
            <Form form={form} layout="vertical" size="small">
              <Row gutter={12}>
                {BILGI.map(([k, ad, zorunlu]) => (
                  <Col key={k} xs={24} md={k === 'adres' || k === 'unvan' ? 12 : 6}>
                    <Form.Item name={k} label={ad} rules={zorunlu ? [{ required: true, whitespace: true }] : []}><Input /></Form.Item>
                  </Col>
                ))}
                <Col xs={24} md={6}><Form.Item name="sira" label="Sıra"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col xs={12} md={6}><Form.Item name="renk_ana" label="Ana renk" rules={[{ required: true }]} getValueFromEvent={renkAl}><RenkSec /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="renk_ikincil" label="İkincil renk" getValueFromEvent={renkAl}><RenkSec allowClear /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="renk_vurgu" label="Vurgu rengi" getValueFromEvent={renkAl}><RenkSec allowClear /></Form.Item></Col>
              </Row>
              <Form.Item label="Renk paleti (portaldaki Renkler bölümü, sıralı)">
                <Form.List name="palet">
                  {(alanlar, { add, remove }) => (
                    <Space wrap>
                      {alanlar.map(({ key, name }) => (
                        <Space key={key} size={4}>
                          <Form.Item name={[name, 'hex']} noStyle rules={[{ required: true }]} getValueFromEvent={renkAl}><RenkSec size="small" /></Form.Item>
                          <Form.Item name={[name, 'name']} noStyle rules={[{ required: true, whitespace: true }]}><Input placeholder="Renk adı" style={{ width: 160 }} /></Form.Item>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Space>
                      ))}
                      {alanlar.length < 12 && <Button size="small" icon={<PlusOutlined />} onClick={() => add({ name: '', hex: '#000000' })}>Renk ekle</Button>}
                    </Space>
                  )}
                </Form.List>
              </Form.Item>
              <Row gutter={12}>
                <Col xs={12} md={6}><Form.Item name="font_ana" label="Ana font" extra="Google Fonts adı ya da aşağıda yüklenen font"><Input /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="font_ikincil" label="İkincil font"><Input /></Form.Item></Col>
                <Col xs={24} md={6}><Form.Item name="font_agirlik" label="Ağırlıklar"><Input placeholder="700 başlıklar, 400 metin" /></Form.Item></Col>
                <Col xs={24} md={6}><Form.Item name="font_not" label="Font notu"><Input /></Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col xs={12} md={6}><Form.Item name="kartvizit_sablon" label="Kartvizit şablonu">
                  <Select options={katalog.kartvizit_sablonlari.map((s) => ({ value: s, label: SABLON_ADI[s] || s }))} />
                </Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="kartvizit_olcu" label="Kartvizit ölçüsü"><Input placeholder="85 × 55 mm" /></Form.Item></Col>
                <Col xs={12} md={4}><Form.Item name="kartvizit_sayfa" label="PDF sayfa"><InputNumber min={1} max={20} style={{ width: '100%' }} /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="kartvizit_not" label="Kartvizit notu"><Input /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="imza_yonelim" label="E-imza logo düzeni">
                  <Select allowClear options={katalog.yonelimler.map((o) => ({ value: o, label: YONELIM_ADI[o] }))} />
                </Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="imza_logo_genislik" label="E-imza logo genişliği (px)"><InputNumber min={40} max={600} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Button type="primary" onClick={kaydet} loading={kaydediliyor}>Kaydet</Button>
            </Form>
          ),
        },
        {
          key: 'logo', label: `Logolar (${logolar.length} düzen)`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              {katalog.yonelimler.map((o) => (
                <LogoKarti key={o} slug={slug} yonelim={o} logo={logolar.find((L) => L.yonelim === o)} dosyalar={dosyalar} onDegisti={degisti} />
              ))}
            </Space>
          ),
        },
        {
          key: 'evrak', label: 'Antetli kâğıt, e-imza, kurumsal set, kartvizit',
          children: (
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <Card size="small" title="Antetli kâğıt" extra={<Text type="secondary">üçü birlikte gerekli</Text>} style={{ marginBottom: 12 }}>
                  {yuva('antetli/docx', 'Word şablonu')}{yuva('antetli/pdf', 'PDF')}{yuva('antetli/onizleme', 'Önizleme (JPG)', { resim: true })}
                </Card>
                <Card size="small" title="E-posta imzası" style={{ marginBottom: 12 }}>
                  {yuva('imza/html', 'HTML')}{yuva('imza/logo', 'Logo (PNG)', { resim: true })}{yuva('imza/hosted', 'Hosted HTML')}
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card size="small" title="Kurumsal set" style={{ marginBottom: 12 }}>
                  {yuva('kurumsal_set/jpg', 'Görsel (JPG)', { resim: true })}{yuva('kurumsal_set/svg', 'Vektör (SVG)')}
                </Card>
                <Card size="small" title="Kartvizit (tasarımcı dosyası)" extra={<Text type="secondary">PDF şart</Text>} style={{ marginBottom: 12 }}>
                  {yuva('kartvizit/pdf', 'Baskı PDF')}{yuva('kartvizit/on', 'Ön yüz', { resim: true })}{yuva('kartvizit/arka', 'Arka yüz', { resim: true })}
                </Card>
              </Col>
            </Row>
          ),
        },
        {
          key: 'urun', label: `Ürün görselleri (${Object.keys(katalog.urunler).filter((u) => dosyalar[`urun/${u}`]?.url).length} / ${Object.keys(katalog.urunler).length})`,
          children: (
            <Row gutter={[16, 4]}>
              {Object.entries(katalog.urunler).map(([u, ad]) => (
                <Col key={u} xs={24} lg={12}>{yuva(`urun/${u}`, dosyalar[`urun/${u}`]?.etiket || ad, { resim: true, urunEtiketi: ad })}</Col>
              ))}
            </Row>
          ),
        },
        {
          key: 'font', label: `Özel fontlar (${fontlar.length})`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">Google Fonts'ta olmayan bir font kullanılıyorsa .woff2 dosyasını burada yükleyin; aile adı, yukarıdaki font alanına yazılanla aynı olmalı.</Text>
              {fontlar.map((y) => yuva(y, y.split('/')[1]))}
              <Space>
                <Input placeholder="Font ailesi (ör. Marka Sans)" value={yeniFont} onChange={(e) => setYeniFont(e.target.value)} style={{ width: 240 }} />
                <Upload accept=".woff2" showUploadList={false} disabled={!/^[A-Za-z0-9 ]{2,60}$/.test(yeniFont)}
                  beforeUpload={(f) => {
                    markaApi.dosyaYukle(slug, `font/${yeniFont.trim()}`, f)
                      .then(() => { message.success('Font yüklendi'); setYeniFont(''); degisti(); })
                      .catch((e) => message.error(hataMesaji(e), 6));
                    return false;
                  }}>
                  <Button icon={<UploadOutlined />} disabled={!/^[A-Za-z0-9 ]{2,60}$/.test(yeniFont)}>woff2 yükle</Button>
                </Upload>
              </Space>
            </Space>
          ),
        },
      ]} />
    </div>
  );
}
